import React, { useEffect, useState } from 'react';
import { Loader2, Ticket, Plus, Power } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const fmtDuration = (c) => {
  if (c.duration === 'forever') return 'Lifetime free';
  if (c.duration === 'once') return '1 month free';
  if (c.duration === 'repeating') return `${c.duration_in_months} months free`;
  return `${c.percent_off ?? 0}% off`;
};
const fmtExpires = (ts) => (ts ? new Date(ts * 1000).toISOString().slice(0, 10) : '—');

export default function AdminPromoTab() {
  const [busy, setBusy] = useState(true);
  const [codes, setCodes] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [form, setForm] = useState({ code: '', kind: 'months', months: 1, max_redemptions: '', expires_days: '' });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get('/admin/promo-codes');
      setCodes(r.data.items || []);
      setRedemptions(r.data.redemptions || []);
    } catch { toast.error('Failed to load promo codes'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (c) => {
    try {
      await api.patch(`/admin/promo-codes/${c.id}`, { active: !c.active });
      setCodes((p) => p.map((x) => (x.id === c.id ? { ...x, active: !x.active } : x)));
      toast.success(`${c.code} ${!c.active ? 'enabled' : 'disabled'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const create = async () => {
    if (!form.code.trim()) { toast.error('Enter a code'); return; }
    setCreating(true);
    try {
      const body = {
        code: form.code.trim(),
        kind: form.kind,
        months: Number(form.months) || 1,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        expires_days: form.expires_days ? Number(form.expires_days) : null,
      };
      await api.post('/admin/promo-codes', body);
      toast.success(`Code ${body.code.toUpperCase()} created`);
      setForm({ code: '', kind: 'months', months: 1, max_redemptions: '', expires_days: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create code'); }
    finally { setCreating(false); }
  };

  if (busy) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--coral))]" /></div>;

  return (
    <div className="space-y-4" data-testid="promos-tab">
      {/* Create */}
      <div className="rounded-2xl bg-white p-5 card-soft">
        <h3 className="flex items-center gap-2 font-extrabold"><Plus className="h-4 w-4 text-[hsl(var(--coral))]" />Create a promo code</h3>
        <p className="mt-1 text-sm text-muted-foreground">Codes apply only to the PRO plan at Stripe checkout. Testers enter them via "Add promotion code".</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-5">
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-muted-foreground">Code</label>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="PURRXYZ" data-testid="promo-create-code" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-3 text-sm font-semibold tracking-wide outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Type</label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="promo-create-kind" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-2 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
              <option value="months">Months free</option>
              <option value="lifetime">Lifetime</option>
            </select>
          </div>
          {form.kind === 'months' && (
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Months</label>
              <input type="number" min={1} value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })} data-testid="promo-create-months" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Max uses</label>
            <input type="number" min={1} value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} placeholder="∞" data-testid="promo-create-max" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Expires (days)</label>
            <input type="number" min={1} value={form.expires_days} onChange={(e) => setForm({ ...form, expires_days: e.target.value })} placeholder="never" data-testid="promo-create-expires" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
          </div>
        </div>
        <button onClick={create} disabled={creating} data-testid="promo-create-btn" className="coral-btn mt-4 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white disabled:opacity-70">
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Create code
        </button>
      </div>

      {/* Codes table */}
      <div className="overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">Code</th><th className="p-3">Offer</th><th className="p-3">Redeemed</th><th className="p-3">Limit</th><th className="p-3">Expires</th><th className="p-3">Status</th></tr>
          </thead>
          <tbody data-testid="promo-codes-table">
            {codes.map((c) => (
              <tr key={c.id} className="border-t border-border">
                <td className="p-3"><span className="rounded bg-accent px-1.5 py-0.5 font-mono text-xs font-bold text-[hsl(var(--coral))]">{c.code}</span>{c.label && <div className="mt-0.5 text-xs text-muted-foreground">{c.label}</div>}</td>
                <td className="p-3">{fmtDuration(c)}</td>
                <td className="p-3 font-semibold">{c.times_redeemed}</td>
                <td className="p-3 text-muted-foreground">{c.max_redemptions ?? '∞'}</td>
                <td className="p-3 text-muted-foreground">{fmtExpires(c.expires_at)}</td>
                <td className="p-3">
                  <button onClick={() => toggle(c)} data-testid={`promo-toggle-${c.code}`} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${c.active ? 'border-green-200 bg-green-50 text-green-700' : 'border-neutral-200 bg-neutral-100 text-neutral-500'}`}>
                    <Power className="h-3 w-3" />{c.active ? 'Active' : 'Disabled'}
                  </button>
                </td>
              </tr>
            ))}
            {!codes.length && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No promo codes yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Redemptions */}
      <div className="rounded-2xl bg-white p-5 card-soft">
        <h3 className="flex items-center gap-2 font-extrabold"><Ticket className="h-4 w-4 text-[hsl(var(--coral))]" />Who redeemed what <span className="text-sm font-semibold text-muted-foreground">({redemptions.length})</span></h3>
        <div className="mt-3 overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
              <tr><th className="p-3">Date</th><th className="p-3">Code</th><th className="p-3">User</th><th className="p-3">Source</th></tr>
            </thead>
            <tbody data-testid="promo-redemptions-table">
              {redemptions.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 text-muted-foreground">{(r.created_at || '').slice(0, 10)}</td>
                  <td className="p-3 font-mono text-xs font-bold">{r.code}</td>
                  <td className="p-3">{r.email || r.user_id}</td>
                  <td className="p-3 text-muted-foreground capitalize">{r.source || 'in-app'}</td>
                </tr>
              ))}
              {!redemptions.length && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No redemptions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
